import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { Producto } from '../../../types/product.types';

@Component({
  selector: 'allas-product-cards',
  imports: [CommonModule, DecimalPipe],
  template: `
    <div class="flex flex-col gap-4 fs-condensed">
      <!-- Título opcional -->
      @if (title) {
      <h1 class="text-2xl sm:text-3xl text-gray-900">
        {{ title }}
      </h1>
      }

      <!-- Carrusel de productos -->
      <div class="relative">
        <!-- Flecha izquierda - Visible en mobile y desktop -->
        <button
          (click)="scrollLeft()"
          class="flex absolute left-0 top-1/2 -translate-y-1/2 z-50 bg-white shadow-lg border border-gray-200 rounded-full w-10 h-10 xl:w-12 xl:h-12 items-center justify-center hover:bg-gray-50 transition-colors duration-200"
          [disabled]="!canScrollLeft"
        >
          <svg
            class="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            ></path>
          </svg>
        </button>

        <!-- Flecha derecha - Visible en mobile y desktop -->
        <button
          (click)="scrollRight()"
          class="flex absolute right-0 top-1/2 -translate-y-1/2 z-50 bg-white shadow-lg border border-gray-200 rounded-full w-10 h-10 xl:w-12 xl:h-12 items-center justify-center hover:bg-gray-50 transition-colors duration-200"
          [disabled]="!canScrollRight"
        >
          <svg
            class="w-6 h-6 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            ></path>
          </svg>
        </button>

        <section
          #scrollContainer
          class="flex gap-3 px-8 xl:px-6 w-full overflow-x-auto scroll-hidden scroll-smooth relative"
          (scroll)="onScroll()"
        >
          @for (producto of productos; track producto.sku) {
          <article class="flex-shrink-0">
            <a
              class="flex flex-col justify-between gap-1 xl:gap-2 rounded-md bg-white shadow-sm border border-gray-200 w-[125px] xl:w-[200px] z-40 hover:scale-95 transition-all duration-300 h-[320px] xl:h-[375px] flex-shrink-0 p-2 xl:p-3"
            >
              <!-- Imagen del producto -->
              <a
                [href]="'/productos/' + producto.slug"
                class="h-1/2 flex items-center justify-center"
              >
                <img
                  [src]="producto.imagenes.thumb[0]"
                  loading="lazy"
                  onerror="this.src='/placeholder.svg'"
                  alt=""
                  class="max-h-full max-w-full object-contain"
                />
              </a>

              <!-- Información del producto -->
              <a
                [href]="'/productos/' + producto.slug"
                class="flex flex-col gap-1"
              >
                <!-- Precio -->
                <h3 class="text-sm xl:text-xl font-black fs-condensed">
                  L. {{ producto.precio | number : '1.2-2' }}
                </h3>

                <!-- Nombre del producto -->
                <p class="text-xs xl:text-sm font-light line-clamp-2">
                  {{ producto.nombre_web }}
                </p>
              </a>

              <!-- Botón de agregar -->
              <button
                (click)="onAddToCart(producto)"
                class="border border-gray-600 text-gray-600 px-2 py-2 rounded-full flex items-center justify-center gap-2 text-xs xl:text-sm font-bold fs-condensed hover:bg-gray-50 transition-colors duration-200"
              >
                <img src="/allas-icons/cart-nav.svg" alt="" class="w-4 h-4" />
                AGREGAR
              </button>
            </a>
          </article>
          }
        </section>
      </div>
    </div>
  `,
  styles: [
    `
      .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .scroll-hidden {
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* Internet Explorer 10+ */
      }

      .scroll-hidden::-webkit-scrollbar {
        display: none; /* WebKit */
      }
    `,
  ],
})
export class ProductCardsComponent implements AfterViewInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>;

  @Input() productos: Producto[] = [];
  @Input() title?: string;
  @Output() addToCart = new EventEmitter<Producto>();

  canScrollLeft = false;
  canScrollRight = false;

  ngAfterViewInit(): void {
    // Verificar estado de scroll después de que la vista se inicialice
    setTimeout(() => this.updateScrollButtons(), 100);
  }

  /**
   * Scroll hacia la izquierda
   */
  scrollLeft(): void {
    if (this.scrollContainer) {
      const scrollAmount = 280 * 4; // Ancho de producto + gap
      this.scrollContainer.nativeElement.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Scroll hacia la derecha
   */
  scrollRight(): void {
    if (this.scrollContainer) {
      const scrollAmount = 280 * 4; // Ancho de producto + gap
      this.scrollContainer.nativeElement.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Maneja el evento de scroll
   */
  onScroll(): void {
    this.updateScrollButtons();
  }

  /**
   * Actualiza el estado de los botones de navegación
   */
  updateScrollButtons(): void {
    if (!this.scrollContainer) return;

    const element = this.scrollContainer.nativeElement;
    this.canScrollLeft = element.scrollLeft > 0;
    this.canScrollRight =
      element.scrollLeft < element.scrollWidth - element.clientWidth;
  }

  /**
   * Genera un array de estrellas basado en el rating
   * @param rating - Rating del producto (1-5)
   * @returns Array de números para las estrellas
   */
  getStars(rating: number): number[] {
    const stars = Math.min(Math.max(Math.round(rating), 1), 5);
    return Array(stars).fill(0);
  }

  /**
   * Emite el evento cuando se hace clic en agregar al carrito
   * @param producto - Producto a agregar al carrito
   */
  onAddToCart(producto: Producto): void {
    this.addToCart.emit(producto);
  }
}
